import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Menu, X, Camera, User, ShoppingBag, LogOut, ClipboardList, Search } from 'lucide-react'
import { useQuery } from 'react-query'
import panierImg from '../assets/panier.jpg'
import { useCart } from '../contexts/CartContext'
import apiService from '../services/api'
import { getImageUrl } from '../utils/apiUrl'
import { getProductUrl } from '../utils/urlUtils'
import packageJson from '../../package.json'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const { totalCount, clearCart } = useCart()
  const [customer, setCustomer] = useState(null)
  const [admin, setAdmin] = useState(null)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const userMenuRef = useRef(null)
  const searchRefMobile = useRef(null)
  const searchRefDesktop = useRef(null)
  const searchInputRef = useRef(null)
  const searchContainerRef = useRef(null)
  const location = useLocation()
  const navigate = useNavigate()

  // Récupérer les données de l'entreprise pour le logo
  const { data: companyData } = useQuery(
    'company-public',
    () => apiService.get('/rentman/company/public').then(res => res.data),
    { staleTime: 5 * 60 * 1000 } // Cache 5 minutes
  )

  // Fonction de recherche dynamique
  const handleSearch = async (query) => {
    if (query.length < 2) {
      setSearchResults([])
      return
    }
    console.log('🔍 Recherche pour:', query)
    setIsSearching(true)
    try {
      const response = await apiService.get('/rentman/products', {
        params: { search: query, limit: 10 }
      })
      console.log('📦 Réponse API:', response.data)
      const products = response.data?.products || []
      console.log('📋 Produits trouvés:', products.length, products)
      setSearchResults(products)
    } catch (error) {
      console.error('❌ Erreur recherche:', error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const submitSearch = () => {
    const q = (searchQuery || '').trim()
    if (!q) return
    navigate(`/catalogue-materiels?search=${encodeURIComponent(q)}`)
    setIsSearchOpen(false)
    setSearchQuery('')
    setSearchResults([])
  }

  // Debounce pour la recherche
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        handleSearch(searchQuery)
      } else {
        setSearchResults([])
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  // Focus sur le champ de recherche quand il s'ouvre
  useEffect(() => {
    if (!isSearchOpen) return
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isSearchOpen])

  // Fermer les résultats de recherche quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      const container = searchContainerRef.current
      if (container && container.contains(event.target)) {
        return // clic à l'intérieur de la zone de recherche
      }
      setSearchResults([])
    }

    if (isSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isSearchOpen])

  useEffect(() => {
    // Charger l'utilisateur courant si un token est présent
    const token = localStorage.getItem('user_token')
    if (!token) {
      return
    }
    apiService.get('/auth/me', { params: { t: Date.now() } })
      .then(res => {
        const u = res.data?.user
        if (!u) {
          return
        }
        if (u.role === 'admin') {
          setAdmin(u)
          setCustomer(null)
        } else {
          setCustomer(u)
          setAdmin(null)
        }
      })
      .catch(() => {
        // Ne pas modifier l'état en cas d'échec transitoire
      })
  }, [location.pathname])

  // Rafraîchir l'état admin/client à chaque changement de page
  useEffect(() => {
    // Synchroniser d'abord depuis le cache local pour éviter le flicker
    try {
      const cached = localStorage.getItem('current_user')
      const u = cached ? JSON.parse(cached) : null
      if (u) {
        if (u.role === 'admin') {
          setAdmin(u)
          setCustomer(null)
        } else {
          setCustomer(u)
          setAdmin(null)
        }
      }
    } catch {}

    const token = localStorage.getItem('user_token')
    if (!token) return
    apiService.get('/auth/me', { params: { t: Date.now() } })
      .then(res => {
        const u = res.data?.user
        if (!u) return // ignorer 304/vides
        if (u.role === 'admin') {
          setAdmin(u)
          setCustomer(null)
          try { localStorage.setItem('current_user', JSON.stringify(u)) } catch {}
        } else {
          setCustomer(u)
          setAdmin(null)
          try { localStorage.removeItem('current_user') } catch {}
        }
      })
      .catch(() => {
        // Ne pas effacer l'état en cas d'échec transitoire
      })
  }, [location.pathname])

  useEffect(() => {
    // Charger l'admin si on est sur une route admin ou si un token admin est présent
    const adminToken = localStorage.getItem('admin_token')
    const onAdminRoute = location.pathname.startsWith('/admin')
    if (adminToken || onAdminRoute) {
      apiService.get('/auth/me')
        .then(res => {
          // Gérer les deux formats de réponse possibles
          const adminData = res.data?.admin || res.data?.user
          setAdmin(adminData || null)
        })
        .catch(() => setAdmin(null))
    } else {
      setAdmin(null)
    }
  }, [location.pathname])

  useEffect(() => {
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false)
      }
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('user_token')
    localStorage.removeItem('customer_token')
    try {
      localStorage.removeItem('rental_start')
      localStorage.removeItem('rental_end')
    } catch {}
    try { clearCart() } catch {}
    setCustomer(null)
    setAdmin(null)
    setIsUserMenuOpen(false)
    navigate('/login')
  }

  const handleAdminLogout = () => {
    localStorage.removeItem('user_token')
    localStorage.removeItem('admin_token')
    localStorage.removeItem('user_role')
    setAdmin(null)
    setCustomer(null)
    setIsUserMenuOpen(false)
    navigate('/admin/login')
  }
  

  const navigation = [
    { name: 'Accueil', href: '/' },
    { name: 'Catalogue', href: '/catalogue-materiels' },
    { name: 'Filmographie', href: '/filmographie' },
    { name: 'Contact', href: '/contact' },
  ]

  const mobileNavigation = [
    { name: 'Filmographie', href: '/filmographie' },
    { name: 'Mentions légales', href: '/mentions-legales' },
    { name: 'CGV', href: '/cgv' },
    { name: 'Politique de confidentialité', href: '/politique-confidentialite' },
  ]

  // Admin link removed from public header

  const isActive = (path) => location.pathname === path

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 backdrop-blur-custom shadow-sm">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <div className="h-10 w-auto group-hover:scale-105 transition-transform duration-300">
              {companyData?.company?.logo_path ? (
                <img 
                  src={`${getImageUrl(companyData.company.logo_path)}?v=${packageJson.version}`}
                  alt={`${companyData.company.name || 'FlightCase'} Logo`}
                  className="h-full w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : (
                <img 
                  src={`${getImageUrl('/images/logo/flightcase-logo.jpg')}?v=${packageJson.version}`}
                  alt="FlightCase Logo" 
                  className="h-full w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              )}
              <div className="h-10 w-10 bg-gray-800 rounded-lg items-center justify-center hidden">
                <Camera className="h-6 w-6 text-white" />
              </div>
            </div>
          </Link>

          {/* Navigation Desktop */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(item.href)
                    ? 'text-gray-800 bg-gray-100'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
          </nav>

          {/* Actions (Panier, Recherche, Mon compte) */}
          <div className="hidden md:flex items-center space-x-2">
            <Link
              to="/panier"
              aria-label="Panier"
              className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
            >
              <div className="relative">
                <img src={panierImg} alt="Panier" className="h-7 w-7 object-contain" />
                {totalCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] bg-logo text-white rounded-full flex items-center justify-center">{totalCount}</span>
                )}
              </div>
            </Link>
            
            {/* Recherche desktop */}
            <button
              onClick={() => {
                setIsSearchOpen(!isSearchOpen)
                setIsUserMenuOpen(false) // Fermer le menu utilisateur si ouvert
              }}
              aria-label="Rechercher des produits"
              className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
            >
              <Search className="h-6 w-6" />
            </button>
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(v => !v)}
                aria-label="Mon compte"
                className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors flex items-center"
              >
                <User className="h-6 w-6" />
                {(() => {
                  const adminLabel = admin ? (admin.username || admin.first_name || admin.email || 'Admin') : ''
                  const customerLabel = customer ? (customer.first_name || customer.username || customer.email) : ''
                  let label = adminLabel || customerLabel || ''
                  if (!label) {
                    const hasToken = Boolean(localStorage.getItem('user_token'))
                    if (hasToken && location.pathname.startsWith('/admin')) label = 'Admin'
                  }
                  return label ? (
                    <span className="text-sm text-gray-700 ml-1">{label}</span>
                  ) : null
                })()}
              </button>
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <div className="py-1">
                    {admin || localStorage.getItem('user_role')==='admin' ? (
                      <>
                        <Link to="/admin" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Dashboard</Link>
                        <Link to="/admin/settings" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Paramètres</Link>
                        <button onClick={handleAdminLogout} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                          <LogOut className="h-4 w-4 mr-2" />
                          Se déconnecter
                        </button>
                      </>
                    ) : customer ? (
                      <>
                        <Link to="/account" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                          <User className="h-4 w-4 mr-2" />
                          Mon compte
                        </Link>
                        <Link to="/account/reservations" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                          <ShoppingBag className="h-4 w-4 mr-2" />
                          Mes réservations
                        </Link>
                        <Link to="/account/favorites" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                          <ClipboardList className="h-4 w-4 mr-2" />
                          Mes listes
                        </Link>
                        <button onClick={handleLogout} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                          <LogOut className="h-4 w-4 mr-2" />
                          Se déconnecter
                        </button>
                      </>
                    ) : (
                      <Link to="/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Se connecter</Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile actions (Panier, Recherche, Menu) */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Panier mobile */}
            <Link
              to="/panier"
              aria-label="Panier"
              className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
            >
              <div className="relative">
                <img src={panierImg} alt="Panier" className="h-6 w-6 object-contain" />
                {totalCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] bg-logo text-white rounded-full flex items-center justify-center">{totalCount}</span>
                )}
              </div>
            </Link>

            {/* Recherche mobile */}
            <button
              onClick={() => {
                setIsSearchOpen(!isSearchOpen)
                setIsMenuOpen(false) // Fermer le menu si ouvert
              }}
              aria-label="Rechercher des produits"
              className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
            >
              <Search className="h-6 w-6" />
            </button>

            {/* Menu burger */}
            <button
              onClick={() => {
                setIsMenuOpen(!isMenuOpen)
                setIsSearchOpen(false) // Fermer la recherche si ouverte
              }}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-gray-50">
            <div className="px-4 py-4 space-y-2">
              {mobileNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 rounded-lg text-base font-medium transition-all duration-200 ${
                    isActive(item.href)
                      ? 'text-gray-800 bg-gray-100'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Barre de recherche sous le header (non flottante) */}
        {isSearchOpen && (
          <div className="border-t border-gray-200 bg-white">
            <div className="container-custom py-3">
              <div className="max-w-2xl mx-auto px-4">
                <div ref={searchContainerRef}>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 pl-4 flex items-center">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Rechercher un produit..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="search-input"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') submitSearch()
                        if (e.key === 'Escape') setIsSearchOpen(false)
                      }}
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <button
                        aria-label="Fermer la recherche"
                        className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsSearchOpen(false)}
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  {/* Résultats sous le champ */}
                  {isSearching && (
                    <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow p-3 text-sm text-gray-500">
                      Recherche...
                    </div>
                  )}
                  {!isSearching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                    <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow p-3 text-sm text-gray-500">
                      Aucun résultat
                    </div>
                  )}
                  {searchResults.length > 0 && (
                    <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow max-h-80 overflow-auto">
                      {searchResults.map((product) => (
                        <div
                          key={product.id}
                          className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          onClick={() => {
                            navigate(getProductUrl(product))
                            setIsSearchOpen(false)
                            setSearchQuery('')
                            setSearchResults([])
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            {product.image_url && (
                              <img
                                src={getImageUrl(product.image_url)}
                                alt={product.name}
                                className="w-10 h-10 object-cover rounded"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                              {product.category_name && (
                                <p className="text-xs text-gray-500 truncate">{product.category_name}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {/* Bouton "Voir plus" */}
                      <div className="p-3 border-t border-gray-100">
                        <button
                          onClick={() => {
                            submitSearch()
                          }}
                          className="w-full text-center text-sm font-medium text-logo hover:text-logo-dark transition-colors"
                        >
                          Voir plus de résultats →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        
      </div>
    </header>
  )
}

export default Header
